import React, { useEffect } from 'react'
import {
    Button,
    Col,
    Form,
    Input,
    notification,
    Row,
    Select,
    Space,
    Typography,
} from 'antd'
import { handleAPIError } from '../../utils/catch-error'
import { IJoinRuleResponse } from '../../services/join-rule/interfaces/join-rule-response.interface'
import { ISourceFileInfo } from '../../services/source-group/interfaces/source-file-info.interface'
import { joinRuleService } from '../../services/join-rule/join-rule.service'
import { sourceFileInfoService } from '../../services/source-group/source-file-info.service'
import { runService } from '../../services/run/run.service'

function CreateJoinRun({ nextStep }: { nextStep: () => void }): JSX.Element {
    const [form] = Form.useForm()
    const [loading, setLoading] = React.useState(false)

    const [joinRules, setJoinRules] = React.useState<IJoinRuleResponse[]>([])
    const [selectedJoinRule, setSelectedJoinRule] =
        React.useState<IJoinRuleResponse>()
    const [leftSourceFiles, setLeftSourceFiles] = React.useState<
        ISourceFileInfo[]
    >([])
    const [rightSourceFiles, setRightSourceFiles] = React.useState<
        ISourceFileInfo[]
    >([])

    useEffect(() => {
        joinRuleService.get().then(setJoinRules).catch(handleAPIError)
    }, [])

    useEffect(() => {
        if (!selectedJoinRule) {
            return
        }
        sourceFileInfoService
            .getAllForDataset(selectedJoinRule.LeftDatasetID)
            .then(setLeftSourceFiles)
            .catch(handleAPIError)
        sourceFileInfoService
            .getAllForDataset(selectedJoinRule.RightDatasetID)
            .then(setRightSourceFiles)
            .catch(handleAPIError)
    }, [selectedJoinRule])

    const onFinish = async (values: any): Promise<void> => {
        setLoading(true)
        try {
            await runService.createJoinRun({
                RunName: values['Run Name'],
                JoinRuleID: values['Join Rule'],
                LeftSourceFileID: values['Left Source Name'],
                RightSourceFileID: values['Right Source Name'],
                OutDatasetName: values['New Dataset Name'],
                OutSourceFileName: values['New File Name'],
            })
            notification.success({
                message: 'Success',
                description: 'Run created successfully',
            })
            form.resetFields()
            setLoading(false)
            nextStep()
        } catch (e) {
            handleAPIError(e)
            setLoading(false)
        }
    }

    const onReset = (): void => {
        form.resetFields()
        setSelectedJoinRule(undefined)
        setLeftSourceFiles([])
        setRightSourceFiles([])
    }

    return (
        <div
            style={{
                paddingTop: '30px',
                margin: '0 auto',
                display: 'table',
                minWidth: '400px',
            }}
        >
            <Form
                layout="vertical"
                form={form}
                name="create-run"
                onFinish={onFinish}
                style={{ maxWidth: '400px' }}
            >
                <Form.Item
                    name="Run Name"
                    label="Run Name"
                    rules={[
                        {
                            required: true,
                            min: 3,
                        },
                    ]}
                >
                    <Input />
                </Form.Item>
                <Form.Item
                    name="Join Rule"
                    label="Join Rule"
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Select
                        showSearch
                        placeholder="Select Join Rule"
                        filterOption={(input, option) =>
                            option?.children
                                // @ts-ignore
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                        onChange={(value: number) => {
                            setSelectedJoinRule(
                                joinRules.find(
                                    (item) => item.JoinRuleID === value
                                )
                            )
                            form.resetFields([
                                'Left Source Name',
                                'Right Source Name',
                            ])
                        }}
                        allowClear
                    >
                        {joinRules.map((item) => (
                            <Select.Option
                                key={item.JoinRuleID}
                                value={item.JoinRuleID}
                            >
                                {item.JoinRuleName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                {selectedJoinRule && (
                    <Typography.Paragraph
                        style={{
                            marginTop: '-16px',
                            fontSize: '13px',
                            marginLeft: '3px',
                        }}
                    >
                        Associated to Data Group:{' '}
                        {selectedJoinRule.SourceGroupName} | Join Type:{' '}
                        {selectedJoinRule.JoinType}
                    </Typography.Paragraph>
                )}
                <Form.Item
                    name="Left Source Name"
                    label={
                        selectedJoinRule
                            ? `Source from dataset ${selectedJoinRule.leftDatasetName}`
                            : 'Left Source Name'
                    }
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Select
                        showSearch
                        placeholder="Select Left Source name"
                        filterOption={(input, option) =>
                            option?.children
                                // @ts-ignore
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                        disabled={!selectedJoinRule}
                    >
                        {leftSourceFiles.map((item) => {
                            return (
                                <Select.Option
                                    key={item.SourceFileID}
                                    value={item.SourceFileID}
                                >
                                    {item.SourceName}
                                </Select.Option>
                            )
                        })}
                    </Select>
                </Form.Item>
                <Form.Item
                    name="Right Source Name"
                    label={
                        selectedJoinRule
                            ? `Source from dataset ${selectedJoinRule.rightDatasetName}`
                            : 'Right Source Name'
                    }
                    rules={[
                        {
                            required: true,
                        },
                    ]}
                >
                    <Select
                        showSearch
                        placeholder="Select Right Source name"
                        filterOption={(input, option) =>
                            option?.children
                                // @ts-ignore
                                ?.toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                        allowClear
                        disabled={!selectedJoinRule}
                    >
                        {rightSourceFiles.map((item) => {
                            return (
                                <Select.Option
                                    key={item.SourceFileID}
                                    value={item.SourceFileID}
                                >
                                    {item.SourceName}
                                </Select.Option>
                            )
                        })}
                    </Select>
                </Form.Item>
                <Row gutter={[20, 0]}>
                    <Col span={12}>
                        <Form.Item
                            name="New Dataset Name"
                            label="New Dataset Name"
                            rules={[
                                {
                                    required: true,
                                    min: 3,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="New File Name"
                            label="New File Name"
                            rules={[
                                {
                                    required: true,
                                    min: 3,
                                },
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
                <Row justify="center">
                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                            >
                                Submit
                            </Button>
                            <Button
                                htmlType="button"
                                onClick={onReset}
                                disabled={loading}
                            >
                                Reset
                            </Button>
                        </Space>
                    </Form.Item>
                </Row>
            </Form>
        </div>
    )
}

export default CreateJoinRun
